import { MODEL_VERSION } from '@/app/constants';
import type { ReferencePrompt } from '@/app/types';

/**
 * 判断是否为图片类型参考（包含上传的图片和图片类型的主体）
 */
export const isImageReference = (ref: ReferencePrompt): boolean => {
  if (ref.type === 'image') return true;
  if (ref.type === 'element') {
    const modality = ref.prompt?.modality;
    return modality === 'image' || !modality;
  }
  return false;
};

/**
 * 判断是否为视频类型参考（包含上传的视频和视频类型的主体）
 */
export const isVideoReference = (ref: ReferencePrompt): boolean => {
  if (ref.type === 'video') return true;
  if (ref.type === 'element') {
    return ref.prompt?.modality === 'video';
  }
  return false;
};

/**
 * 判断是否为文字类型主体
 */
export const isTextElementReference = (ref: ReferencePrompt): boolean => {
  return ref.type === 'element' && ref.prompt?.modality === 'text';
};

/**
 * 获取图片和图片主体的总数
 */
export const getImageAndImageElementCount = (referencePrompts: ReferencePrompt[]): number => {
  return referencePrompts.filter(isImageReference).length;
};

/**
 * 获取视频和视频主体的总数
 */
export const getVideoAndVideoElementCount = (referencePrompts: ReferencePrompt[]): number => {
  return referencePrompts.filter(isVideoReference).length;
};

/**
 * 获取指定版本下允许选择的最大视频主体数量
 * @description Q2Pro 版本最多允许 2 个视频主体，但如果已有视频时长 >= 5s，则最多只能有 1 个。其他版本不支持视频主体（0个）。
 */
export const getMaxVideoCount = (modelVersion: string | undefined, referencePrompts: ReferencePrompt[]): number => {
  if (modelVersion !== MODEL_VERSION.Q2_PRO) {
    return 0;
  }

  const videoRefs = referencePrompts.filter(isVideoReference);
  if (videoRefs.length > 0) {
    const firstVideo = videoRefs[0];
    let duration = 0;

    if (firstVideo.type === 'video') {
      duration = firstVideo.prompt?.duration || 0;
    } else if (firstVideo.type === 'element') {
      // 视频主体当作 4 秒处理
      duration = 4;
    }

    if (duration >= 5) {
      return 1;
    }
    return 2;
  }

  return 1;
};

/**
 * 获取文字主体的总数
 */
export const getTextElementCount = (referencePrompts: ReferencePrompt[]): number => {
  return referencePrompts.filter(isTextElementReference).length;
};
